import { Injectable, effect, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { ModalService } from 'app/shared/services/modal/modal.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private auth = inject(AuthService);
  private modal = inject(ModalService);

  private readonly IDLE_LIMIT_MS = 5 * 60 * 60 * 1000; // 5h
  private readonly WARNING_WINDOW_MS = 2 * 60 * 1000; // 2 min
  // Mínimo intervalo entre escrituras de actividad en localStorage.
  // Evita saturar el storage (y a las otras pestañas) con cada mousemove.
  private readonly WRITE_THROTTLE_MS = 5 * 1000; // 5s

  // Fuente de verdad compartida entre pestañas: timestamp de la última actividad.
  private readonly LAST_ACTIVITY_KEY = 'lastActivity';

  private idleTimeoutId: number | null = null;
  private logoutTimeoutId: number | null = null;
  private warningIntervalId: number | null = null;

  private isWarningActive = false;
  // Última vez que esta pestaña escribió en localStorage (para el throttle).
  private lastWriteMs = 0;

  constructor() {
    if (typeof window === 'undefined') return;

    this.registerActivityListeners();
    this.listenRemoteActivity();

    // Arrancar / detener timers según el estado de autenticación
    effect(() => {
      const status = this.auth.authStatus();

      if (status === 'authenticated') {
        // Al autenticarse (login o restauración de sesión) se marca actividad
        // fresca para no heredar un timestamp viejo de una sesión anterior.
        this.markActivityNow();
      } else {
        this.modal.closeModal('info-logout-modal');
        this.clearAllTimers();
      }
    });
  }

  private registerActivityListeners(): void {
    const onActivity = () => {
      // Si está el modal de advertencia activo, ignora la actividad:
      // solo el botón "Seguir conectado" mantiene la sesión.
      if (this.isWarningActive) return;
      this.markActivity();
    };

    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('scroll', onActivity, true);
    window.addEventListener('touchstart', onActivity);
    window.addEventListener('focus', onActivity);
  }

  // Escucha actividad publicada por OTRAS pestañas (evento "storage").
  private listenRemoteActivity(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this.LAST_ACTIVITY_KEY) return;
      this.onRemoteActivity();
    });
  }

  // --- Actividad compartida -------------------------------------------------

  private readLastActivity(): number {
    const raw = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;

    if (Number.isNaN(parsed)) {
      // Sin valor previo: inicializa con "ahora".
      const now = Date.now();
      this.writeLastActivity(now);
      return now;
    }

    return parsed;
  }

  private writeLastActivity(ts: number): void {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, String(ts));
  }

  // Actividad local: escribe el timestamp compartido (con throttle) y re-arma el timer.
  private markActivity(): void {
    if (this.auth.authStatus() !== 'authenticated') return;

    const now = Date.now();
    if (now - this.lastWriteMs < this.WRITE_THROTTLE_MS) return; // throttled

    this.lastWriteMs = now;
    this.writeLastActivity(now);
    this.resetIdleTimer();
  }

  // Marca actividad de inmediato, ignorando el throttle (login, "Seguir conectado").
  private markActivityNow(): void {
    if (this.auth.authStatus() !== 'authenticated') return;

    const now = Date.now();
    this.lastWriteMs = now;
    this.writeLastActivity(now);
    this.resetIdleTimer();
  }

  // Otra pestaña reportó actividad: cancela cualquier advertencia local y re-arma.
  private onRemoteActivity(): void {
    if (this.auth.authStatus() !== 'authenticated') return;

    if (this.isWarningActive) {
      this.modal.closeModal('info-logout-modal');
    }
    // resetIdleTimer recalcula contra el timestamp compartido (no vuelve a escribir).
    this.resetIdleTimer();
  }

  // --- Timers ---------------------------------------------------------------

  private clearIdleTimer(): void {
    if (this.idleTimeoutId !== null) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  private clearLogoutTimer(): void {
    if (this.logoutTimeoutId !== null) {
      window.clearTimeout(this.logoutTimeoutId);
      this.logoutTimeoutId = null;
    }
  }

  private clearWarningInterval(): void {
    if (this.warningIntervalId !== null) {
      window.clearInterval(this.warningIntervalId);
      this.warningIntervalId = null;
    }
  }

  private clearAllTimers(): void {
    this.clearIdleTimer();
    this.clearLogoutTimer();
    this.clearWarningInterval();
    this.isWarningActive = false;
  }

  private resetIdleTimer(): void {
    // Si no está autenticado, no se cuenta inactividad
    if (this.auth.authStatus() !== 'authenticated') {
      this.clearAllTimers();
      return;
    }

    this.clearAllTimers();

    // Tiempo restante medido contra la última actividad COMPARTIDA entre pestañas.
    const elapsed = Date.now() - this.readLastActivity();
    const remaining = this.IDLE_LIMIT_MS - elapsed;

    if (remaining <= 0) {
      this.openConfirmationLogoutModal();
      return;
    }

    this.idleTimeoutId = window.setTimeout(() => {
      this.openConfirmationLogoutModal();
    }, remaining);
  }

  private handleIdleLogout(): void {
    this.clearAllTimers();
    this.modal.closeModal('info-logout-modal');

    this.modal.openModal('logout-modal', {
      title: 'Por seguridad, cerramos tu sesión.',
      message:
        'Estuviste inactivo por un tiempo y cerramos tu sesión automáticamente.<br><br><strong>Vuelve a iniciar sesión para continuar.</strong>',
      onConfirm: () => {
        // El modal se cierra,
      },
    });

    this.auth.logout();
  }

  private openConfirmationLogoutModal(): void {
    this.isWarningActive = true;
    let remainingMs = this.WARNING_WINDOW_MS;

    this.updateWarningModal(remainingMs);

    // Actualizar el contador cada segundo
    this.warningIntervalId = window.setInterval(() => {
      remainingMs -= 1000;

      if (remainingMs <= 0) {
        // Se agotó el tiempo: se apaga el intervalo
        this.clearWarningInterval();
        return;
      }

      this.updateWarningModal(remainingMs);
    }, 1000);

    // Programar el logout automático al final de la ventana de 2 min
    this.logoutTimeoutId = window.setTimeout(() => {
      this.handleIdleLogout();
    }, this.WARNING_WINDOW_MS);
  }

  private updateWarningModal(remainingMs: number): void {
    const timeText = this.formatTime(remainingMs);

    this.modal.openModal('info-logout-modal', {
      title: 'Alerta por inactividad.',
      message: `Tu sesión se cerrará automáticamente en: <strong>${timeText}</strong>`,
      onConfirm: () => {
        // "Seguir conectado": marca actividad fresca y la propaga a las demás
        // pestañas (vía el evento "storage" sobre LAST_ACTIVITY_KEY).
        this.isWarningActive = false;
        this.clearWarningInterval();
        this.clearLogoutTimer();
        this.markActivityNow();
        return;
      },
    });
  }

  // Formatea ms -> "mm:ss"
  private formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${this.pad2(minutes)}:${this.pad2(seconds)}`;
  }

  private pad2(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }
}
